import { registerAuditLabels } from "@vixio/ui";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo measurement (ver modules/measurement/README.md). */

export type StudyStatus = "planning" | "collecting" | "closed";

export const STUDY_STATUS: StatusMap = {
  planning: { label: "planejamento", tone: "neutral" },
  collecting: { label: "em coleta", tone: "info" },
  closed: { label: "encerrado", tone: "ok" },
};

export const AUTH_STATUS: StatusMap = {
  active: { label: "ativa", tone: "ok" },
  revoked: { label: "revogada", tone: "crit" },
  expired: { label: "vencida", tone: "warn" },
};

export const OCC_CODE_RE = /^[A-Z]{1,4}-[0-9]{1,4}$/;

export interface Authorization {
  id: string;
  org_id: string;
  client_org_name: string;
  client_org_ref: string | null;
  signed_by_name: string;
  signed_by_role: string;
  scope: string;
  valid_until: string;
  document_ref: string | null;
  granted_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  created_at: string;
}

export function authStatus(a: Pick<Authorization, "revoked_at" | "valid_until">, today: string): "active" | "revoked" | "expired" {
  if (a.revoked_at) return "revoked";
  if (a.valid_until < today) return "expired";
  return "active";
}

export interface StudyRow {
  id: string;
  org_id: string;
  process_name: string;
  unit_label: string;
  description: string | null;
  status: StudyStatus;
  round: 1 | 2;
  baseline_study_id: string | null;
  started_on: string | null;
  closed_on: string | null;
  authorization_id: string;
  client_org_name: string;
  authorization_valid_until: string;
  authorization_status: "active" | "revoked" | "expired";
  n_stages: number;
  n_occurrences: number;
  n_exceptions: number;
  followup_due_on: string | null;
  followup_study_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Stage {
  id: string;
  study_id: string;
  position: number;
  name: string;
}

export interface Occurrence {
  id: string;
  study_id: string;
  code: string;
  observed_on: string;
  is_exception: boolean;
  exception_reason: string | null;
  created_at: string;
}

export interface StageRecord {
  id: string;
  occurrence_id: string;
  stage_id: string;
  wait_minutes: number | null;
  exec_minutes: number | null;
  returned: boolean;
  return_reason: string | null;
}

export interface Dist {
  n: number;
  median: number | null;
  p25: number | null;
  p75: number | null;
  max: number | null;
}

export interface StageStats {
  position: number;
  name: string;
  n: number;
  median_total: number | null;
  p25: number | null;
  p75: number | null;
  max_wait: number | null;
  median_wait: number | null;
  median_exec: number | null;
  returns_count: number;
  returns_rate: number | null;
  low_n_warning: boolean;
}

export interface Stats {
  n_occurrences: number;
  n_occurrences_total: number;
  n_exceptions: number;
  exceptions_included: boolean;
  min_recommended_n: number;
  low_n_warning: boolean;
  lead_time: Dist;
  stages: StageStats[];
}

type Cmp = Pick<StageStats, "median_total" | "p25" | "p75" | "median_wait" | "median_exec" | "max_wait" | "returns_rate">;
export interface Compare {
  baseline_n: number;
  followup_n: number;
  low_n_warning: boolean;
  warnings: string[];
  lead_time: { baseline: Dist; followup: Dist; delta_median: number | null; delta_p25: number | null; delta_p75: number | null };
  stages: Array<{ position: number; name: string; baseline: StageStats; followup: StageStats | null; delta: Cmp; delta_pct_median_total: number | null; low_n_warning: boolean }>;
}

export function fmtMin(v: number | null | undefined): string {
  if (v === null || v === undefined) return "-";
  const n = Number(v);
  if (Math.abs(n) >= 120) {
    const h = Math.floor(Math.abs(n) / 60);
    const m = Math.round(Math.abs(n) % 60);
    return `${n < 0 ? "−" : ""}${h} h ${String(m).padStart(2, "0")}`;
  }
  return `${n < 0 ? "−" : ""}${Math.abs(n).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} min`;
}

export function fmtDeltaMin(v: number | null | undefined): string {
  if (v === null || v === undefined) return "-";
  const n = Number(v);
  return n > 0 ? `+${fmtMin(n)}` : fmtMin(n);
}

export function fmtRate(v: number | null | undefined): string {
  return v === null || v === undefined ? "-" : `${(Number(v) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

export interface RecordInput {
  stage_id: string;
  wait_minutes?: number;
  exec_minutes?: number;
  returned?: boolean;
  return_reason?: string;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "measurement.create_authorization": {
      args: {
        p_org: string;
        p_client_org_name: string;
        p_signed_by_name: string;
        p_signed_by_role: string;
        p_scope: string;
        p_valid_until: string;
        p_client_org_ref?: string | null;
        p_document_ref?: string | null;
        p_granted_at?: string | null;
      };
      returns: string;
    };
    "measurement.revoke_authorization": { args: { p_authorization_id: string; p_reason: string }; returns: null };
    "measurement.create_study": { args: { p_authorization_id: string; p_process_name: string; p_unit_label: string; p_description?: string | null }; returns: string };
    "measurement.set_stages": { args: { p_study_id: string; p_names: string[] }; returns: null };
    "measurement.start_collection": { args: { p_study_id: string }; returns: null };
    "measurement.add_occurrence": {
      args: { p_study_id: string; p_code: string; p_observed_on: string; p_records: RecordInput[]; p_is_exception?: boolean; p_exception_reason?: string | null };
      returns: string;
    };
    "measurement.close_study": { args: { p_study_id: string }; returns: null };
    "measurement.create_followup": { args: { p_baseline_study_id: string; p_force?: boolean; p_force_reason?: string | null }; returns: string };
    "measurement.stats": { args: { p_study_id: string; p_include_exceptions?: boolean }; returns: Stats };
    "measurement.compare": { args: { p_followup_study_id: string; p_include_exceptions?: boolean }; returns: Compare };
    "measurement.pii_reason": { args: { p_text: string }; returns: string | null };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "observation_authorization.granted": "Autorização de observação registrada",
  "observation_authorization.revoked": "Autorização de observação revogada",
  "study.created": "Estudo de medição criado",
  "study.stages_set": "Etapas definidas",
  "study.collection_started": "Coleta iniciada",
  "occurrence.added": "Ocorrência registrada",
  "study.closed": "Estudo encerrado",
  "study.followup_created": "Reaplicação criada",
  "study.followup_forced": "Reaplicação antecipada",
});
